import {
  type MeterReadings,
  type MonthKey,
  monthEnd,
  monthStart,
  type ResultOf,
} from '@horof/shared';
import { graphql } from '../gql';

/**
 * Everything the month close checks and links, for one month: the readings
 * (connected meters and their bills this month), the billed totals, bills
 * worth a second look, and the signed print/share links.
 */
export const MonthCloseDocument = graphql(`
  query MonthClose($start: Date!, $end: Date!, $month: String!) {
    meters(
      filter: { meterStatus: { equalTo: CONNECTED } }
      orderBy: [SORT_ORDER_ASC, METER_NO_ASC]
    ) {
      nodes {
        id
        label
        member {
          id
          fullName
        }
        monthBills: bills(
          filter: { billMonth: { greaterThanOrEqualTo: $start, lessThanOrEqualTo: $end } }
          orderBy: [CREATED_AT_ASC]
        ) {
          nodes {
            id
            currentReading
            billTotal
            paidAmount
            createdAt
          }
        }
      }
    }
    report: monthlyReport(filter: { month: { equalTo: $start } }) {
      nodes {
        billsCount
        billedTotal
        consumption
      }
    }
    zeroConsumption: bills(
      filter: {
        billMonth: { greaterThanOrEqualTo: $start, lessThanOrEqualTo: $end }
        consumption: { equalTo: 0 }
      }
    ) {
      totalCount
    }
    highConsumption: bills(
      filter: {
        billMonth: { greaterThanOrEqualTo: $start, lessThanOrEqualTo: $end }
        consumption: { greaterThan: 50 }
      }
    ) {
      nodes {
        id
        previousReading
        currentReading
        consumption
        billTotal
        readingPhoto {
          id
        }
        member {
          id
          fullName
        }
        meter {
          id
          label
        }
      }
    }
    billsLink: monthlyBillsLink(month: $month)
    receiptsLink: monthlyReceiptsLink(month: $month)
    unreachableNoticesLink: noticesLink(onlyUnreachable: true)
    allNoticesLink: noticesLink
    bulletinLink(month: $month)
  }
`);

export const monthVariables = (month: MonthKey) => ({
  start: monthStart(month),
  end: monthEnd(month),
  month,
});

type MeterNode = NonNullable<
  NonNullable<ResultOf<typeof MonthCloseDocument>['meters']>['nodes'][number]
>;
export type MonthBill = NonNullable<MeterNode['monthBills']['nodes'][number]>;
export type MeterStatus = MeterReadings<MonthBill>;
